import DeviceTypes from '@/enums/DeviceTypes';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { deviceType } from './DeviceUtils';

/**
 * Converts base64 string to a blob
 *
 * @param {String} base64String base64 string to convert
 * @param {String} contentType target content type e.g. "application/pdf"
 * @returns {Blob} File blob with given content type
 */
export const convertBase64ToBlob = (base64String, contentType) => {
  const byteChars = atob(base64String);
  const uInt8Array = new Uint8Array(byteChars.length);

  for (let i = 0; i < uInt8Array.length; ++i) {
    uInt8Array[i] = byteChars.charCodeAt(i);
  }

  return new Blob([uInt8Array], { type: contentType });
};

/**
 * This function is used to open PDFs in a new tab(on desktop) OR initiate PDF downloads on other device types(mobile, tablet, etc).
 *
 * @param {string} fileData File contents as a string
 * @param {string} [fileName="document"] File name to offer in download prompt on mobile. Uses `document.pdf` by default.
 * @param {string[]} urlStorage Array to store the created url object containing the pdf file
 */
export const initiatePdfFileDownload = (fileData, fileName = 'document', urlStorage) => {
  // NOTE: If you need to expand this function in the future to accommodate more devices / browsers, it might be worth considering using some library(eg.FileSaverJS) to manage downloads instead.

  const device = deviceType();

  // blob type needs to be application/octet-stream to make sure mobile browsers offer to download the file correctly.
  const blob = device === DeviceTypes.DESKTOP ? convertBase64ToBlob(fileData, 'application/pdf') : convertBase64ToBlob(fileData, 'application/octet-stream');

  const element = document.createElement('a');

  const newUrl = URL.createObjectURL(blob);

  element.setAttribute('href', newUrl);

  if (device === DeviceTypes.DESKTOP) {
    // opening new window from async functions works on desktop
    element.setAttribute('target', '_blank');
  } else {
    // opening new window from async functions doesn't work on mobile, hence initiate download instead
    element.setAttribute('download', `${fileName}.pdf`); // use filename here
  }

  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);

  // Adding url to storage to allow parent function to unload URL for optimal performance and memory usage
  if (urlStorage) urlStorage.push(newUrl);
};

/**
 * Temporarily patches window.getComputedStyle so that any CSS property returning an
 * empty string is exposed as `undefined` instead.
 *
 * html2canvas reads computed CSS values via named accessors (e.g. `style.content`) and
 * feeds them straight into its own CSS tokenizer. When a browser returns an empty string
 * for an unset / unsupported / vendor-prefixed property, the tokenizer produces only an
 * EOF token and html2canvas throws "Error parsing CSS component value, unexpected EOF".
 * Returning `undefined` makes html2canvas fall back to each property's initial value.
 *
 * @returns {Function} A restore function that reverts window.getComputedStyle.
 */
const patchGetComputedStyleForHtml2Canvas = () => {
  const originalGetComputedStyle = window.getComputedStyle.bind(window);

  window.getComputedStyle = (element, pseudoElt) => {
    const declaration = originalGetComputedStyle(element, pseudoElt);

    return new Proxy(declaration, {
      get(target, prop) {
        const value = target[prop];
        if (typeof value === 'function') {
          return value.bind(target);
        }
        if (value === '') {
          return undefined;
        }
        return value;
      },
    });
  };

  return () => {
    window.getComputedStyle = originalGetComputedStyle;
  };
};

/**
 * This function is used to convert a form (or any html) to a PDF and return it as a pdf data URI.
 *
 * @param {Vue|HTMLElement} element Vue component reference or HTMLElement to convert to PDF
 * @param {boolean} [isPlainHtml=false] Flag to indicate if the element is a plain HTML element or a Vue component. Optional, defaults to false.
 * @returns {Promise<string>} Data URI of the generated PDF
 */
export const convertFormToPdfDataUri = async (element, isPlainHtml = false) => {
  const restoreGetComputedStyle = patchGetComputedStyleForHtml2Canvas();

  let canvas;
  try {
    canvas = await html2canvas(isPlainHtml ? element : element.$el);
  } finally {
    restoreGetComputedStyle();
  }

  const marginLeft = 24;
  const img = canvas.toDataURL('image/png');

  // eslint-disable-next-line new-cap
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'px',
    format: [canvas.width + marginLeft, canvas.height],
  });

  doc.addImage(img, 'PNG', marginLeft, 0, canvas.width, canvas.height, '', 'FAST');

  // doc.save(`download.pdf`); // This can be enabled to download the PDF directly to test the PDF generation

  return doc.output('datauristring').split(',')[1];
};
